import { DataTable, FormGroupHeader } from "@/components/shared";
import { CarDetails, TenderPermissionEnum } from "@/graphql/__generated__/graphql";
import { getColumnsCars } from "./columns";
import { useIntl } from "react-intl";
import { useParams, useOutletContext } from "react-router-dom";
import { useRemoveCarMutationHook } from "@/hooks";
import { ConfirmRemoveAlert } from "../Dialog";
import { ActionButtonConfig } from "@/components/shared/FormGroupHeader";
import { useState } from "react";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import { useTranslation } from "react-i18next";
import AddCar from "../AddCar/Form";
import EditCarDialog from "./EditCarDialog";
import { useFetchCarsOnTenderQueryHook, useFetchServicesOnTenderQueryHook } from "@/hooks/queries";

interface CarsTableProps {
  /** Whether to show with header (for standalone page) or inline (for embedding in Services) */
  showHeader?: boolean;
  /** Custom title for the section */
  title?: string;
  /** Custom description for the section */
  description?: string;
}

/**
 * CarsTable component for displaying and managing cars in a tender.
 * Provides a table view with the ability to add, edit, and remove cars.
 *
 * @component
 * @returns {JSX.Element} The rendered CarsTable component
 */
const CarsTable = ({ showHeader = true, title, description }: CarsTableProps) => {
  // Hooks
  const intl = useIntl();
  const { tenderID } = useParams<{ tenderID: string }>();
  const { tenderByIdData } = useOutletContext<EditTenderContext>();
  const { t } = useTranslation(["translation", "common", "tables", "components"]);

  // Mutation hooks
  const { removeCarMutation, loading: removeCarLoading } = useRemoveCarMutationHook();

  // Query hooks
  const { data: carsData, loading: carsLoading } = useFetchCarsOnTenderQueryHook(Number(tenderID));
  const { data: servicesData } = useFetchServicesOnTenderQueryHook(Number(tenderID), false, true);

  // States
  const [currentCarDetails, setCurrentCarDetails] = useState<CarDetails | null>(null);
  const [showConfirmRemoveAlert, setShowConfirmRemoveAlert] = useState<boolean>(false);
  const [showAddCarModal, setShowAddCarModal] = useState<boolean>(false);
  const [showEditCarModal, setShowEditCarModal] = useState<boolean>(false);

  // Constants
  const canAddCar = tenderByIdData?.fetchTenderById?.permissions.includes(TenderPermissionEnum.AddService);
  const carDetails = (carsData?.fetchCarsOnTender ?? []) as CarDetails[];
  const services = servicesData?.fetchServicesOnTender?.services ?? [];

  /**
   * Handler for clicking the edit button on a car row
   * @param {CarDetails} carDetails - The car details to be edited
   */
  const handleOnClickEdit = (carDetails: CarDetails) => {
    setCurrentCarDetails(carDetails);
    setShowEditCarModal(true);
  };

  /**
   * Handler for clicking the remove button on a car row
   * @param {CarDetails} carDetails - The car details to be removed
   */
  const handleOnClickRemove = (carDetails: CarDetails) => {
    setCurrentCarDetails(carDetails);
    setShowConfirmRemoveAlert(true);
  };

  /**
   * Handler for removing a car
   */
  const handleRemoveCar = () => {
    if (!currentCarDetails) return;

    removeCarMutation({
      variables: {
        tenderId: Number(tenderID),
        carId: currentCarDetails.car.id,
      },
    });
    setShowConfirmRemoveAlert(false);
    setCurrentCarDetails(null);
  };

  // Action buttons configuration
  const actionButtons: ActionButtonConfig[] = [
    canAddCar
      ? {
        text: t("edittender.cars.addcar"),
        actionFn: () => setShowAddCarModal(true),
        variant: "default" as const,
      }
      : null,
  ].filter(Boolean) as ActionButtonConfig[];

  return (
    <>
      {showHeader && (
        <FormGroupHeader
          actionButtons={actionButtons}
          title={title || t("edittender.cars.title")}
          description={description || t("edittender.cars.description")}
          className="pr-4 pl-2"
        />
      )}

      {/* Cars table */}
      <div className={showHeader ? "pr-4 pl-2 pb-[300px]" : ""}>
        <DataTable
          disableRowColour={true}
          loading={carsLoading}
          displayAmount={carDetails.length !== 0 ? carDetails.length : 5}
          data={carDetails}
          columns={getColumnsCars(intl, t, !!canAddCar, handleOnClickEdit, handleOnClickRemove, removeCarLoading, services)}
          className="max-h-[60vh]"
          enableColumnPinning={true}
          columnPinning={{ left: [], right: ["actions"] }}
        />
      </div>

      {/* Confirm Remove Car Alert */}
      <ConfirmRemoveAlert
        open={showConfirmRemoveAlert}
        onOpenChange={setShowConfirmRemoveAlert}
        actionButtonFn={handleRemoveCar}
        bodyText={t("components:alertdialogs.removecar")}
      />

      {/* Add Car Modal */}
      <AddCar open={showAddCarModal} onOpenChange={setShowAddCarModal} />

      {/* Edit Car Modal - Full edit with delete-create pattern */}
      <EditCarDialog
        open={showEditCarModal}
        onOpenChange={setShowEditCarModal}
        carDetails={currentCarDetails}
      />
    </>
  );
};

export default CarsTable;
